import { readAdminCredentialRevision } from './storage/admin-credential-revision';

/**
 * In-flight request deduplication.
 * Prevents concurrent duplicate API calls by coalescing requests with the same key.
 */

const inFlightRequests = new Map<string, Promise<unknown>>();

/**
 * Execute a function with deduplication. If a request with the same key is already
 * in progress, returns the existing Promise instead of starting a new execution.
 *
 * @param key - Unique key identifying this request (e.g., "openai:1h:1706745600:1706832000")
 * @param fn - The async function to execute if no in-flight request exists
 * @returns The result of the function execution
 */
export async function dedup<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inFlightRequests.get(key);
  if (existing) {
    return existing as Promise<T>;
  }

  const promise = fn().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, promise);
  return promise;
}

/**
 * Build a dedup key for segment cache operations.
 */
export function buildSegmentDedupKey(
  provider: 'openai' | 'anthropic',
  bucketWidth: string,
  startTime: number,
  endTime: number,
  credentialRevision: string = readAdminCredentialRevision()
): string {
  return `${provider}:${credentialRevision}:${bucketWidth}:${startTime}:${endTime}`;
}

/**
 * Check how many requests are currently in-flight.
 * Useful for debugging and monitoring.
 */
export function getInFlightCount(): number {
  return inFlightRequests.size;
}
